<template>
    <div class="columns is-centered is-mobile" v-if="!initDone">
        <LoadingSpinner2 class="has-added-margin" />
    </div>
    <div v-else class="columns is-multiline">
        <div class="column is-12">
            <h3 class="has-text-weight-bold">{{ translate("page.registration_stage.manage_stages") }}</h3>
        </div>

        <div class="column is-12">
            <div class="box is-condensed mb-5">
                <p class="mb-2">{{ translate("common.options") }}:</p>
                <div class="field mb-2">
                    <Checkbox2 v-model="allowEditCategoryAfterDueDate">
                        {{ translate("page.registration_stage.allow_edit_category_after_due_date") }}
                    </Checkbox2>
                </div>
                <div class="field mb-2">
                    <div class="is-flex is-align-items-center">
                        <Checkbox2 :disabled="!allowEditCategoryAfterDueDate" v-model="afterDueChangesAllowedUntil" class="mr-3">
                            {{ translate("page.registration_stage.allowed_until") }}:
                        </Checkbox2>

                        <NativeDateTimePicker
                            v-model="afterDueDateUntilDatetime"
                            :class="{ 'is-danger': datetimeValidationError }"
                            :disabled="!afterDueChangesAllowedUntil"
                            class="input is-small"
                            style="width: fit-content"
                        />
                        <p v-if="datetimeValidationError" class="ml-3 help is-danger">
                            {{ translate("flash_messages.invalid_datetime") }}
                        </p>
                    </div>
                </div>

                <div class="field">
                    <Checkbox2 :disabled="!allowEditCategoryAfterDueDate" v-model="allowChangesOnlyInSingleCompetitorCategories">
                        {{ translate("page.registration_stage.allow_changes_only_in_single_competitor_categories") }}
                    </Checkbox2>
                </div>

                <LoadingButton :is-loading="savingOptionsInProgress" class="button is-small" @click="saveOptions()">
                    {{ translate("page.registration_stage.save_options") }}
                </LoadingButton>
            </div>

            <div
                v-for="(registrationStage, index) in registrationStages"
                :key="index"
                class="box is-flex is-condensed is-align-items-center mb-3"
            >
                <div class="mr-4">
                    <div>#{{ index + 1 }}</div>
                </div>

                <div class="is-flex-grow-1">
                    <div>
                        <span class="is-description">{{ translate("page.registration_stage.start") }}:</span>
                        {{ convertToTimezone(registrationStage.startDate, eventTimezone) }}
                    </div>
                    <div>
                        <span class="is-description">{{ translate("page.registration_stage.end") }}:</span>
                        {{ convertToTimezone(registrationStage.endDate, eventTimezone) }}
                    </div>
                    <div>
                        <span class="is-description">{{ translate("page.registration_stage.total_days") }}: </span>
                        {{ registrationStage.totalDays }}
                    </div>
                </div>

                <div>
                    <div class="buttons are-small">
                        <button class="button" @click="openEditModal(registrationStage)">
                            {{ translate("common.edit") }}
                        </button>
                        <button class="button is-danger" @click="deleteRegistrationStage(registrationStage.id)">
                            {{ translate("common.delete") }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="column is-12">
            <div class="has-top-border mb-5"></div>

            <div class="buttons">
                <button class="button is-primary" @click="openAddModal">
                    <span class="icon">
                        <i class="fas fa-plus-circle"></i>
                    </span>
                    <span>
                        {{ translate("page.registration_stage.add_stage") }}
                    </span>
                </button>
            </div>
        </div>

        <AddEditRegistrationStageModal ref="addEditModal" :event-id="eventId" :event-timezone="eventTimezone" @saved="onModalSaved" />
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, watch } from "vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import { useEditEventStore } from "../editEventStore";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import AddEditRegistrationStageModal from "./AddEditRegistrationStageModal.vue";
    import registrationStagesApi, { RegistrationStage } from "../../Api/registrationStages";
    import { handleError } from "../../Common/errorHandler";
    import { translate } from "../../Common/translations";
    import { convertFromTimezone, convertToTimezone } from "../../Common/dateTimeUtils";
    import Checkbox2 from "../../Common/Checkbox2.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { successToast } from "../../Common/toasts";
    import NativeDateTimePicker from "../../Common/NativeDateTimePicker.vue";

    const store = useEditEventStore();
    const eventId = computed(() => store.eventId);
    const eventTimezone = computed(() => store.event.timezone);

    const initDone = ref(false);
    const registrationStages = ref<Array<RegistrationStage>>([]);
    const addEditModal = ref<ComponentExposed<typeof AddEditRegistrationStageModal>>();

    const allowEditCategoryAfterDueDate = ref(false);
    const afterDueChangesAllowedUntil = ref(false);
    const afterDueDateUntilDatetime = ref("");
    const allowChangesOnlyInSingleCompetitorCategories = ref(false);
    const datetimeValidationError = ref(false);

    const fetchAll = () => {
        return registrationStagesApi
            .getAll(eventId.value)
            .then(stages => {
                registrationStages.value = stages;
            })
            .catch(handleError);
    };

    const fetchOptions = () => {
        return registrationStagesApi
            .getOptions(eventId.value)
            .then(options => {
                allowEditCategoryAfterDueDate.value = options.allowEditRegistrationsAfterDueDate;
                allowChangesOnlyInSingleCompetitorCategories.value = options.allowChangesOnlyInSingleCompetitorCategories;

                if (options.allowEditRegistrationsAfterDueDateUntil) {
                    afterDueChangesAllowedUntil.value = true;
                    afterDueDateUntilDatetime.value = convertToTimezone(
                        options.allowEditRegistrationsAfterDueDateUntil,
                        eventTimezone.value,
                    );
                } else {
                    afterDueChangesAllowedUntil.value = false;
                    afterDueDateUntilDatetime.value = "";
                }
            })
            .catch(handleError);
    };

    onMounted(() => {
        Promise.all([fetchAll(), fetchOptions()]).finally(() => {
            initDone.value = true;
        });
    });

    const openAddModal = () => {
        addEditModal.value?.open();
    };

    const openEditModal = (registrationStage: RegistrationStage) => {
        addEditModal.value?.open(registrationStage);
    };

    const onModalSaved = () => {
        fetchAll();
    };

    const deleteRegistrationStage = (registrationStageId: number) => {
        if (!confirm(translate("common.are_you_sure"))) {
            return;
        }

        return registrationStagesApi.removeRegistrationStage(eventId.value, registrationStageId).then(fetchAll).catch(handleError);
    };

    const savingOptionsInProgress = ref(false);

    watch(afterDueDateUntilDatetime, () => {
        if (datetimeValidationError.value) {
            datetimeValidationError.value = false;
        }
    });

    function saveOptions() {
        datetimeValidationError.value = false;

        if (afterDueChangesAllowedUntil.value) {
            const datetimePattern = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}.*$/; //The asterisk allows for optional seconds
            if (!afterDueDateUntilDatetime.value || !datetimePattern.test(afterDueDateUntilDatetime.value)) {
                datetimeValidationError.value = true;
                return;
            }
        }

        savingOptionsInProgress.value = true;
        const utcUntil = afterDueChangesAllowedUntil.value
            ? convertFromTimezone(afterDueDateUntilDatetime.value, eventTimezone.value)
            : null;
        return registrationStagesApi
            .saveRegistrationStagesOptions({
                eventId: store.eventId,
                allowEditRegistrationsAfterDueDate: allowEditCategoryAfterDueDate.value,
                allowEditRegistrationsAfterDueDateUntil: utcUntil,
                allowChangesOnlyInSingleCompetitorCategories: allowChangesOnlyInSingleCompetitorCategories.value,
            })
            .then(() => {
                successToast(translate("common.changes_have_been_saved"));
            })
            .catch(handleError)
            .finally(() => {
                savingOptionsInProgress.value = false;
            });
    }
</script>

<style scoped>
    .has-top-border {
        border-top: 1px dotted gray;
    }
</style>
